import React from 'react';
import { FilterOption, getOperator, isNullOperator, OpenSelect, StringConditionType, TextInput } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, SxProps } from '@mui/material';
import { isJSON } from '@utils/utils';
import TokenFilterInput from './TokenFilterInput/TokenFilterInput';
import { TokenSuggestion } from './TokenFilterInput/types';

const FilterInput = ({
  numberField,
  onChange,
  operator,
  value,
  options,
  size = 'small',
  placeholder = 'Type Value',
  width,
  inputWidth,
  inputMinWidth,
  disabled = isNullOperator(operator),
  error,
  optionsUseQueryProps,
  skeletonLoading = false,
  endAdornment,
  readOnly,
  sx,
  autoFocus,
  tokenSuggestions,
  onTokenSelect,
  valueOptions,
}: {
  numberField: boolean;
  operator: string | undefined;
  value: unknown;
  onChange: (value: unknown) => void;
  size?: 'small' | 'medium' | 'xSmall';
  placeholder?: string;
  width?: number;
  inputWidth?: number | string;
  inputMinWidth?: number;
  options: FilterOption[];
  disabled?: boolean;
  error?: unknown;
  optionsUseQueryProps?: { options: FilterOption[]; isLoading: boolean };
  skeletonLoading?: boolean;
  endAdornment?: React.ReactNode;
  readOnly?: boolean;
  tokenSuggestions?: TokenSuggestion[];
  onTokenSelect?: (token: TokenSuggestion) => void;
  sx?: SxProps;
  autoFocus?: boolean;
  valueOptions?: { value: string; title: string }[];
}) => {
  const inputOptions = operator && options.find(({ value }) => value === getOperator(operator))?.inputOptions;

  if (inputOptions) {
    // When inputWidth is '100%' (fullWidth mode), share the row via flex:1 instead of both sides
    // claiming 100% width.
    const sideSize = inputWidth === '100%' ? { flex: 1, minWidth: inputMinWidth } : { width: inputWidth, minWidth: inputMinWidth };
    return (
      <Box sx={{ ...flex.itemsCenter, gap: 1, width }}>
        <TextInput
          sx={{ ...sideSize, ...inputOptions.sx1 }}
          size={size}
          placeholder={inputOptions.placeholder1 || placeholder}
          value={defaultToEmpty(() => inputOptions.getValue1(value))}
          disableClearButton
          type={numberField ? 'number' : 'text'}
          onChange={newVal => onChange(inputOptions.onChange1(newVal, value))}
          error={error && inputOptions.getValue1(error)}
          inputProps={{ endAdornment, readOnly }}
        />
        {inputOptions.separator}
        <TextInput
          sx={{ ...sideSize, ...inputOptions.sx2 }}
          size={size}
          placeholder={inputOptions.placeholder2 || placeholder}
          value={defaultToEmpty(() => inputOptions.getValue2(value))}
          disableClearButton
          onChange={newVal => onChange(inputOptions.onChange2(newVal, value))}
          error={error && inputOptions.getValue2(error)}
          inputProps={{ endAdornment, readOnly }}
          type={numberField ? 'number' : 'text'}
        />
      </Box>
    );
  }

  if (getOperator(operator) === 'in' && valueOptions?.length) {
    return (
      <OpenSelect
        style={{ width: inputWidth, minWidth: inputMinWidth }}
        size={size}
        options={valueOptions}
        isMultiple
        value={value || []}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        disableClearButton
        readOnly={readOnly}
      />
    );
  }

  if (optionsUseQueryProps && [StringConditionType.EQUALS, StringConditionType.NOT_EQUAL].includes(operator as StringConditionType)) {
    return (
      <OpenSelect
        style={{ width: inputWidth, minWidth: inputMinWidth }}
        size={size}
        options={optionsUseQueryProps.options}
        loading={optionsUseQueryProps.isLoading}
        skeletonLoading={skeletonLoading}
        value={(value as string | number | null) || null}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        disableClearButton
        isRequired
        optionsUseQueryProps={optionsUseQueryProps}
        readOnly={readOnly}
      />
    );
  }

  if (tokenSuggestions?.length) {
    return (
      <TokenFilterInput
        value={value as string | undefined}
        onChange={onChange}
        onTokenSelect={onTokenSelect}
        tokenSuggestions={tokenSuggestions}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        error={error as string | string[] | undefined}
        inputWidth={inputWidth}
      />
    );
  }

  return (
    <TextInput
      sx={{ width: inputWidth, minWidth: inputMinWidth, ...sx }}
      autoFocus={autoFocus}
      size={size}
      placeholder={placeholder}
      value={isJSON(value) ? '' : (value as string | number | null)}
      type={numberField ? 'number' : 'text'}
      disableClearButton={numberField}
      disabled={disabled}
      onChange={onChange}
      error={error}
      inputProps={{ endAdornment, readOnly }}
    />
  );
};

export default FilterInput;

function defaultToEmpty(getValue) {
  try {
    return getValue() || '';
  } catch {
    return '';
  }
}
